import { ChangeEvent, FormEvent, KeyboardEvent, Ref, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, Download, ImagePlus, Save, Trash2, Upload } from 'lucide-react'
import { Acordeon, AcordeonItem, Alerta, AreaTexto, Boton, Campo, Cargando, Dialogo } from '../kit'
import { useDescribirFoto, useEliminarFoto, useFotos, useSubirFotos } from '../../queries/useInventario'
import { urlDescargaFoto, urlFoto, urlMiniatura, urlZipFotos } from '../../services/fotoService'
import { CarpetaFotos, FichaBien, FotoBien } from '../../types/inventario'
import { mensajeDeError } from '../../utils/errores'
import { formatoFecha } from '../../utils/fecha'

// Los mismos límites que core/fotos.py; el servidor los vuelve a revisar.
const MAX_MB = 15
const MAX_POR_VEZ = 30
const TIPOS = 'image/jpeg,image/png,image/webp'

const MES = new Intl.DateTimeFormat('es-CL', { month: 'long' })
const dia = (iso: string) => {
  const [a, m, d] = iso.split('-').map(Number)
  return new Date(a, m - 1, d) // fecha local: sin corrimiento por zona horaria
}

/** '3 al 9 de mayo de 2026', '28 de abril al 4 de mayo de 2026'. */
export function rangoCarpeta(c: Pick<CarpetaFotos, 'inicio' | 'fin'>): string {
  const [i, f] = [dia(c.inicio), dia(c.fin)]
  const fin = `${f.getDate()} de ${MES.format(f)} de ${f.getFullYear()}`
  if (i.getFullYear() !== f.getFullYear()) return `${i.getDate()} de ${MES.format(i)} de ${i.getFullYear()} al ${fin}`
  if (i.getMonth() !== f.getMonth()) return `${i.getDate()} de ${MES.format(i)} al ${fin}`
  return `${i.getDate()} al ${fin}`
}

const cuantas = (n: number) => `${n} ${n === 1 ? 'foto' : 'fotos'}`

/**
 * Las fotos del bien, en una sección que parte cerrada. Adentro, las carpetas de 7 días
 * (la más reciente abierta) con sus miniaturas; se suben varias a la vez, y cada una se
 * ve en grande, se describe, se descarga o se borra. «Descargar» baja un ZIP de todas o
 * de una carpeta.
 */
/** Lo que la ficha puede pedirle a la galería: abrir el selector de fotos (su botón «Subir fotos»). */
export interface ControlGaleria {
  subir: () => void
}

/** `soloLectura`: para quien no administra bienes; ve y descarga, pero no sube, describe ni borra. */
export function GaleriaFotos({ bien, ref, soloLectura = false }: { bien: FichaBien; ref?: Ref<ControlGaleria>; soloLectura?: boolean }) {
  const [abierta, setAbierta] = useState(false)
  const carpetas = useFotos(bien.id, abierta)
  const [carpetaAbierta, setCarpetaAbierta] = useState<string | null>(null)
  const [porSubir, setPorSubir] = useState<File[] | null>(null)
  const [viendo, setViendo] = useState<number | null>(null)
  const entrada = useRef<HTMLInputElement>(null)
  useImperativeHandle(ref, () => ({ subir: () => entrada.current?.click() }), [])
  const lista = carpetas.data ?? []
  const todas = useMemo(() => lista.flatMap((c) => c.fotos), [lista])

  // Al abrir la galería, la carpeta más reciente queda abierta.
  useEffect(() => {
    if (carpetaAbierta === null && lista.length) setCarpetaAbierta(lista[0].inicio)
  }, [lista, carpetaAbierta])

  const elegir = (e: ChangeEvent<HTMLInputElement>) => {
    const archivos = [...(e.target.files ?? [])]
    e.target.value = '' // para poder elegir de nuevo los mismos
    if (archivos.length) setPorSubir(archivos)
  }

  return (
    <>
      {/* Fuera del acordeón: la cabecera de la ficha lo abre aunque la galería esté cerrada. */}
      <input ref={entrada} type="file" accept={TIPOS} multiple hidden onChange={elegir} />
      <Acordeon ancho className="galeria">
        <AcordeonItem
          titulo={<>Fotos <span className="galeria__cuenta">{bien.n_fotos ? cuantas(bien.n_fotos) : 'ninguna todavía'}</span></>}
          abierto={abierta}
          onAlternar={() => setAbierta(!abierta)}
        >
          <div className="galeria__cuerpo">
            <div className="galeria__barra">
              <p className="texto-secundario">Se agrupan en carpetas de 7 días, desde la primera foto de cada carpeta.</p>
              <div className="acciones-pagina">
                {!soloLectura && <Boton icono={ImagePlus} onClick={() => entrada.current?.click()}>Subir fotos</Boton>}
                {todas.length > 0 && (
                  <Boton variante="outlined" icono={Download} href={urlZipFotos(bien.id)}>Descargar todas</Boton>
                )}
              </div>
            </div>
            {carpetas.isLoading && <Cargando />}
            {carpetas.isError && <Alerta tipo="error">{mensajeDeError(carpetas.error)}</Alerta>}
            {carpetas.data && !lista.length && <p className="texto-secundario">Todavía no hay fotos de este bien.</p>}
            {lista.length > 0 && (
              <Acordeon ancho className="galeria__carpetas">
                {lista.map((c) => (
                  <AcordeonItem
                    key={c.inicio}
                    titulo={<>{rangoCarpeta(c)} <span className="galeria__cuenta">{cuantas(c.fotos.length)}</span></>}
                    abierto={carpetaAbierta === c.inicio}
                    onAlternar={() => setCarpetaAbierta(carpetaAbierta === c.inicio ? '' : c.inicio)}
                  >
                    <div className="galeria__carpeta">
                      <ul className="galeria__grilla">
                        {c.fotos.map((f) => (
                          <li key={f.id}>
                            <button type="button" className="galeria__foto" onClick={() => setViendo(f.id)} aria-label={`Ver ${f.nombre}`}>
                              <img src={urlMiniatura(f.id)} alt={f.observacion || f.nombre} loading="lazy" />
                              {f.observacion && <span className="galeria__obs">{f.observacion}</span>}
                            </button>
                          </li>
                        ))}
                      </ul>
                      <div>
                        <Boton tam="sm" variante="text" icono={Download} href={urlZipFotos(bien.id, c.inicio)}>Descargar esta carpeta</Boton>
                      </div>
                    </div>
                  </AcordeonItem>
                ))}
              </Acordeon>
            )}
          </div>
        </AcordeonItem>
      </Acordeon>

      {porSubir && (
        <DialogoSubirFotos
          bien={bien.id}
          archivos={porSubir}
          onCerrar={() => setPorSubir(null)}
          onSubidas={(nuevas) => {
            setAbierta(true)
            setCarpetaAbierta(nuevas[0]?.inicio ?? null)
          }}
        />
      )}
      {viendo !== null && <DialogoFoto fotos={todas} id={viendo} onCambiar={setViendo} soloLectura={soloLectura} onCerrar={() => setViendo(null)} />}
    </>
  )
}

/** Vista previa de lo elegido, una observación para todas y la subida con su avance. */
function DialogoSubirFotos({
  bien, archivos, onCerrar, onSubidas,
}: {
  bien: number
  archivos: File[]
  onCerrar: () => void
  onSubidas: (carpetas: CarpetaFotos[]) => void
}) {
  const subir = useSubirFotos()
  const [observacion, setObservacion] = useState('')
  const [avance, setAvance] = useState(0)
  // Las URL de la vista previa se crean y se liberan en el mismo efecto.
  const [vistas, setVistas] = useState<string[]>([])
  useEffect(() => {
    const urls = archivos.map((a) => URL.createObjectURL(a))
    setVistas(urls)
    return () => urls.forEach((u) => URL.revokeObjectURL(u))
  }, [archivos])

  const pesadas = archivos.filter((a) => a.size > MAX_MB * 1024 * 1024).map((a) => a.name)
  const problema =
    archivos.length > MAX_POR_VEZ
      ? `Elegiste ${archivos.length}: sube a lo más ${MAX_POR_VEZ} a la vez.`
      : pesadas.length
        ? `Pesan más de ${MAX_MB} MB: ${pesadas.join(', ')}.`
        : null

  const enviar = (e: FormEvent) => {
    e.preventDefault()
    subir.mutate(
      { bien, archivos, observacion, alAvanzar: setAvance },
      {
        onSuccess: (carpetas) => {
          onSubidas(carpetas)
          onCerrar()
        },
      },
    )
  }

  return (
    <Dialogo abierto onCerrar={onCerrar} titulo={`Subir ${cuantas(archivos.length)}`}>
      <form className="pila" onSubmit={enviar}>
        <ul className="galeria__grilla galeria__grilla--previa">
          {archivos.map((a, i) => (
            <li key={`${a.name}-${i}`}>
              <img className="galeria__previa" src={vistas[i]} alt={a.name} />
            </li>
          ))}
        </ul>
        <Campo etiqueta="Observación" ayuda="Opcional. Queda en todas estas fotos; después se puede cambiar en cada una.">
          <AreaTexto rows={2} value={observacion} onChange={(e) => setObservacion(e.target.value)} data-autofocus />
        </Campo>
        {problema && <Alerta tipo="error">{problema}</Alerta>}
        {subir.isError && <Alerta tipo="error">{mensajeDeError(subir.error)}</Alerta>}
        <div className="acciones-formulario">
          <Boton variante="text" onClick={onCerrar}>Cancelar</Boton>
          <Boton type="submit" icono={Upload} cargando={subir.isPending} disabled={!!problema}>
            {subir.isPending ? `Subiendo… ${avance} %` : 'Subir'}
          </Boton>
        </div>
      </form>
    </Dialogo>
  )
}

/** Una foto en grande: su observación, descargarla, borrarla y pasar a la anterior o la siguiente. */
function DialogoFoto({
  fotos, id, onCambiar, onCerrar, soloLectura,
}: {
  fotos: FotoBien[]
  id: number
  onCambiar: (id: number) => void
  onCerrar: () => void
  soloLectura: boolean
}) {
  const describir = useDescribirFoto()
  const eliminar = useEliminarFoto()
  const i = fotos.findIndex((f) => f.id === id)
  const foto = fotos[i]
  const [observacion, setObservacion] = useState(foto?.observacion ?? '')
  const [borrando, setBorrando] = useState(false)

  useEffect(() => {
    setObservacion(foto?.observacion ?? '')
    setBorrando(false)
    describir.reset()
  }, [id]) // al cambiar de foto
  if (!foto) return null

  const ir = (paso: number) => {
    const otra = fotos[i + paso]
    if (otra) onCambiar(otra.id)
  }
  const teclas = (e: KeyboardEvent) => {
    if ((e.target as HTMLElement).closest('textarea, input')) return
    if (e.key === 'ArrowLeft') ir(-1)
    if (e.key === 'ArrowRight') ir(1)
  }
  const guardar = (e: FormEvent) => {
    e.preventDefault()
    describir.mutate({ id: foto.id, observacion })
  }
  const borrar = () =>
    eliminar.mutate(foto.id, {
      onSuccess: () => {
        const siguiente = fotos[i + 1] ?? fotos[i - 1]
        if (siguiente) onCambiar(siguiente.id)
        else onCerrar()
      },
    })

  return (
    <Dialogo abierto ancho onCerrar={onCerrar} titulo={`Foto ${i + 1} de ${fotos.length}`}>
      <div className="pila" onKeyDown={teclas}>
        <div className="foto-visor">
          <Boton variante="text" tam="sm" icono={ChevronLeft} aria-label="Foto anterior" disabled={i === 0} onClick={() => ir(-1)} />
          <img className="foto-grande" src={urlFoto(foto.id)} alt={foto.observacion || foto.nombre} />
          <Boton variante="text" tam="sm" icono={ChevronRight} aria-label="Foto siguiente" disabled={i === fotos.length - 1} onClick={() => ir(1)} />
        </div>
        <p className="texto-secundario">
          {foto.nombre} · subida el {formatoFecha(foto.fecha)}
          {foto.usuario && ` por ${foto.usuario}`}
        </p>
        {soloLectura ? (
          <div className="pila">
            {foto.observacion && <p className="texto-preformateado">{foto.observacion}</p>}
            <div className="acciones-formulario">
              <Boton variante="outlined" icono={Download} href={urlDescargaFoto(foto.id)}>Descargar</Boton>
            </div>
          </div>
        ) : (
        <form className="pila" onSubmit={guardar}>
          <Campo etiqueta="Observación">
            <AreaTexto rows={2} value={observacion} onChange={(e) => setObservacion(e.target.value)} />
          </Campo>
          {describir.isSuccess && observacion === foto.observacion && <Alerta tipo="success">Observación guardada.</Alerta>}
          {(describir.isError || eliminar.isError) && <Alerta tipo="error">{mensajeDeError(describir.error ?? eliminar.error)}</Alerta>}
          {borrando ? (
            <Alerta tipo="error" titulo="¿Borrar esta foto?">
              Se borra para siempre; en el historial del bien queda que se borró.
              <div className="acciones-formulario">
                <Boton variante="text" tam="sm" onClick={() => setBorrando(false)}>Cancelar</Boton>
                <Boton color="error" tam="sm" icono={Trash2} onClick={borrar} cargando={eliminar.isPending}>Borrar</Boton>
              </div>
            </Alerta>
          ) : (
            <div className="acciones-formulario">
              <Boton variante="text" color="error" icono={Trash2} onClick={() => setBorrando(true)}>Borrar</Boton>
              <Boton variante="outlined" icono={Download} href={urlDescargaFoto(foto.id)}>Descargar</Boton>
              <Boton type="submit" icono={Save} cargando={describir.isPending} disabled={observacion.trim() === foto.observacion}>
                Guardar
              </Boton>
            </div>
          )}
        </form>
        )}
      </div>
    </Dialogo>
  )
}
